import { Descriptions, Input, Select, Typography } from "antd";
import { useState } from "react";
import dayjs from "dayjs";
import {
  companyAssetField,
  companyDepartmentField,
  companyRoleField,
  corporateContactFields,
  companyUserLocalizationFields,
} from "../../controls/userProfileFields";
import { FieldConfig, renderFields } from "../../controls/DescriptionsFields";
import AssetMetricsView from "../AssetMetricsView";

export interface CompanyProfileSectionProps {
  readonly data: Record<string, any> | null;
  readonly editing: boolean;
  /** Click-to-confirm handler for the membership's company phone. */
  readonly companyPhoneOnConfirm?: () => void;
  /** Extra membership fields appended after localization (e.g. employee gender / internal+external IDs). */
  readonly extraFields?: FieldConfig[];
  /** Forward seam: fired when the active company membership is switched. Unwired today (a corporate
   *  user has exactly one membership); becomes the switcher handler when multi-company lands. */
  readonly onCompanyChange?: (companyUid: string) => void;
}

/**
 * TIER 2 — the user's company-membership ("Company Profile") as one block: a searchable company
 * SELECTOR + the membership data (department / role / company phone / country / language / joined
 * date / extras) editable beneath.
 *
 * DEFERRED (structure-for-later): today a corporate user has exactly ONE `CompanyUserProfile`, so the
 * selector resolves to a single option and `onCompanyChange` is unwired. The real switcher — a list of
 * the user's profiles + per-company fetch/edit on change — waits for a genuine multi-membership use
 * case. The `CompanyUserProfile` table is already keyed by (user, company), so that upgrade is bounded
 * and needs no layout change here.
 */
function CompanyProfileSection(props: CompanyProfileSectionProps) {
  const { data, editing } = props;
  const dt = (value: any) => (value ? dayjs(value).format("DD/MM/YYYY HH:mm") : undefined);

  // Per-asset metrics drill (opened from the read-mode Asset link).
  const [metricsOpen, setMetricsOpen] = useState(false);
  const [metricsAsset, setMetricsAsset] = useState<{ uid?: string; name?: string }>({});
  const onViewMetrics = (assetUid: string, assetName?: string) => {
    setMetricsAsset({ uid: assetUid, name: assetName });
    setMetricsOpen(true);
  };

  // Count-driven company switcher: build options from a `companies` membership array when the backend
  // provides one (multi-company), else the single membership. The interactive selector renders only
  // when there is more than one — so it auto-enables when multi-company lands, with no code change here.
  const companyOptions: { value: string; label?: string }[] = Array.isArray(
    (data as any)?.companies,
  )
    ? (data as any).companies.map((c: any) => ({
        value: c.companyUid,
        label: c.companyName,
      }))
    : data?.companyUid
      ? [{ value: data.companyUid, label: data.companyName }]
      : [];

  // Editable membership fields (company phone, localization, role-specific extras).
  const editableFields: FieldConfig[] = [
    ...corporateContactFields(data, {
      editable: true,
      companyPhone: { onConfirm: props.companyPhoneOnConfirm },
    }),
    ...companyUserLocalizationFields(data, { editable: true }),
    ...(props.extraFields ?? []),
  ];

  // Free-text — kept last and full-width so it has room.
  const aboutFields: FieldConfig[] = [
    {
      key: "aboutMe",
      label: "About",
      editable: true,
      span: 2,
      input: <Input.TextArea size="small" rows={3} />,
    },
  ];

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row items-center justify-between">
        <Typography.Text strong>Company Profile</Typography.Text>
        {companyOptions.length > 1 ? (
          <Select
            size="small"
            style={{ minWidth: 220 }}
            showSearch
            optionFilterProp="label"
            placeholder="Company"
            value={data?.companyUid ?? undefined}
            onChange={(value) => props.onCompanyChange?.(value)}
            options={companyOptions}
          />
        ) : (
          <Typography.Text type="secondary">{data?.companyName}</Typography.Text>
        )}
      </div>
      <Descriptions
        bordered
        size="small"
        column={2}
        items={[
          { key: "tenantName", label: "Tenant", children: data?.tenantName },
          // Department, Asset and Role are the person's organisation placement, and all three are now
          // editable — §4.2 requires "role assignment", and until this unit role and department were
          // written only by the registration wizards, so nobody could be promoted or transferred after
          // they were created. Changing the Role can move the person between the Managers and Employees
          // tabs, because those two lists are defined by the role's IsManager flag.
          // Admin requirement 2026-11-03 — Company → Department → Role →
          // Asset order so operators see placement in the order they actually
          // decide it (company first, then where in that company, then
          // what the person does, then which physical asset they're on).
          ...renderFields(
            [
              companyDepartmentField(data, { editable: true }),
              companyRoleField(data, { editable: true }),
              companyAssetField(data, { editable: true, onViewMetrics }),
            ],
            data,
            editing,
          ),
          ...renderFields(editableFields, data, editing),
          { key: "joinedDate", label: "Joined Date", span: 2, children: dt(data?.joinedDate) },
          ...renderFields(aboutFields, data, editing),
        ]}
      />
      <AssetMetricsView
        open={metricsOpen}
        onClose={() => setMetricsOpen(false)}
        assetUid={metricsAsset.uid}
        assetName={metricsAsset.name}
      />
    </div>
  );
}

export default CompanyProfileSection;
